import React, { useEffect, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import { Theme, createStyles, makeStyles, useTheme } from '@material-ui/core/styles';
import { Button, Container } from '@michelin/acid-components';
import { usePermissions, useTranslation } from '@michelin/central-provider';
import { PageTitle } from 'components/v3/PageTitle/PageTitle';
import { useUIVersion } from 'contexts/UIVersionContext';
import { ServiceOfferIds, getBaseUrl } from 'prefs-and-service-offers';
import { useHistory } from 'react-router-dom';
import { BillingApprovalSubArea, useBillingApprovalAreaStatus } from './subAreas/billingApproval';
import { StatusTone } from './subAreas/components';
import { ContactsSubArea, useContactsAreaStatus } from './subAreas/contacts';
import { MechanicalSubArea, useMechanicalAreaStatus } from './subAreas/mechanical';
import { OverviewSubArea } from './subAreas/overview';
import { TireSubArea, useTireAreaStatus } from './subAreas/tire';

type AreaKey = 'overview' | 'tire' | 'mechanical' | 'billing' | 'contacts';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    layout: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '1.25rem',
      padding: '1.25rem',
      minHeight: 'calc(100dvh - 65px)',
      backgroundColor: theme.palette.background.default,
    },
    nav: {
      flexShrink: 0,
      width: 232,
      position: 'sticky',
      top: '1.25rem',
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      padding: '1rem 0.75rem',
    },
    navHeading: {
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      padding: '0 0.5rem',
      marginBottom: '0.75rem',
    },
    navItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.7rem',
      width: '100%',
      padding: '0.55rem 0.6rem',
      marginBottom: '0.35rem',
      borderRadius: 10,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      textAlign: 'left',
      font: 'inherit',
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    navItemSelected: {
      backgroundColor: '#e8eefb',
      '&:hover': {
        backgroundColor: '#e8eefb',
      },
    },
    navIcon: {
      flexShrink: 0,
      width: 34,
      height: 34,
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 17,
    },
    navText: {
      flex: 1,
      minWidth: 0,
    },
    navLabel: {
      fontSize: '0.85rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    navStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: 5,
      marginTop: 2,
      fontSize: '0.72rem',
      color: theme.palette.text.secondary,
    },
    navStatusDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      marginRight: 4,
    },
    navFooter: {
      marginTop: '1rem',
      paddingTop: '1rem',
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    navFooterText: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      marginBottom: '0.75rem',
    },
    legacyButton: {
      textTransform: 'none',
      width: '100%',
    },
    content: {
      flex: 1,
    },
  }),
);

const TONE_DOT: Record<StatusTone, string> = {
  success: '#1e7e34',
  warning: '#b26a00',
  danger: '#c0392b',
  neutral: '#9ca3af',
};

interface NavArea {
  key: AreaKey;
  label: string;
  icon: string;
  iconColor: string;
  status: { label: string; tone: StatusTone };
}

const NAV_AREAS: NavArea[] = [
  {
    key: 'overview',
    label: 'Overview',
    icon: '\u2630',
    iconColor: '#0b2e59',
    status: { label: '3 items need attention', tone: 'danger' },
  },
  {
    key: 'tire',
    label: 'Tire',
    icon: '\u25CB',
    iconColor: '#e8eefb',
    status: { label: '1 type missing', tone: 'warning' },
  },
  {
    key: 'mechanical',
    label: 'Mechanical',
    icon: '\u{1F527}',
    iconColor: '#fdf3c9',
    status: { label: 'Complete', tone: 'success' },
  },
  {
    key: 'billing',
    label: 'Billing & Approval',
    icon: '\u{1F4B0}',
    iconColor: '#fdf3c9',
    status: { label: 'Limit missing', tone: 'danger' },
  },
  {
    key: 'contacts',
    label: 'Contacts',
    icon: '\u{1F465}',
    iconColor: '#f3d9e6',
    status: { label: '1 incomplete', tone: 'warning' },
  },
];

const AREA_COMPONENTS: Record<AreaKey, React.ComponentType> = {
  overview: OverviewSubArea,
  tire: TireSubArea,
  mechanical: () => <MechanicalSubArea />,
  billing: BillingApprovalSubArea,
  contacts: ContactsSubArea,
};

export function MainPreferencesModule() {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const permissions = usePermissions();
  const { isNewUI, toggleUIVersion } = useUIVersion();

  const urbanOnlyFleet = !!(
    permissions.location &&
    permissions.location.is_urban &&
    !permissions.location.is_commercial &&
    !permissions.location.override_is_commercial
  );
  const legacyServiceOffer = urbanOnlyFleet ? ServiceOfferIds.bulkOrder : ServiceOfferIds.onCall;
  const openLegacyLocations = () => {
    if (isNewUI) toggleUIVersion();
    history.push(`${getBaseUrl(false)}/${legacyServiceOffer}/locations`);
  };

  // Local selection scaffold - Overview is shown by default. Real page
  // linking / routing is wired up separately.
  const [selectedArea, setSelectedArea] = useState<AreaKey>('overview');
  const ActiveArea = AREA_COMPONENTS[selectedArea];

  const { headerStatus: contactsStatus, incompleteCount, refreshStatus: refreshContactsStatus } = useContactsAreaStatus();
  const { headerStatus: tireStatus, baseMissingReasons, refreshStatus: refreshTireStatus } = useTireAreaStatus();
  const { headerStatus: mechanicalStatus } = useMechanicalAreaStatus();
  const {
    headerStatus: billingStatus,
    incompleteCount: billingIncompleteCount,
    refreshStatus: refreshBillingStatus,
  } = useBillingApprovalAreaStatus();

  useEffect(() => {
    void refreshContactsStatus();
    void refreshTireStatus();
    void refreshBillingStatus();
  }, [refreshBillingStatus, refreshContactsStatus, refreshTireStatus]);
  const overviewMissingCount = incompleteCount + baseMissingReasons.length + billingIncompleteCount;
  const overviewStatus: { label: string; tone: StatusTone } =
    overviewMissingCount > 0
      ? {
          label: t('{{count}} items need attention', { count: overviewMissingCount }),
          tone: 'danger',
        }
      : { label: t('Complete'), tone: 'success' };

  const toneDotColors: Record<StatusTone, string> = {
    ...TONE_DOT,
    neutral: theme.palette.text.secondary,
  };

  const navAreas = NAV_AREAS.map((area) => {
    if (area.key === 'overview') return { ...area, status: overviewStatus };
    if (area.key === 'contacts') return { ...area, status: contactsStatus };
    if (area.key === 'tire') return { ...area, status: tireStatus };
    if (area.key === 'mechanical') return { ...area, status: mechanicalStatus };
    if (area.key === 'billing') return { ...area, status: billingStatus };
    return area;
  });

  const activeAreaElement =
    selectedArea === 'mechanical' ? (
      <MechanicalSubArea onOpenBillingApproval={() => setSelectedArea('billing')} />
    ) : (
      <ActiveArea />
    );

  return (
    <>
      <PageTitle
        eyebrow={t('Account Setup')}
        title={t('Main Preferences')}
        subtitle={t("Your fleet's key service preferences - consolidated in one place")}
      />
      <Container>
        <div className={classes.layout}>
          <nav className={classes.nav}>
            <Typography className={classes.navHeading}>{t('Preference Areas')}</Typography>
            {navAreas.map((area) => {
              const selected = area.key === selectedArea;
              return (
                <button
                  type="button"
                  key={area.key}
                  className={`${classes.navItem} ${selected ? classes.navItemSelected : ''}`}
                  onClick={() => setSelectedArea(area.key)}
                >
                  <span className={classes.navIcon} style={{ backgroundColor: area.iconColor }} aria-hidden>
                    {area.icon}
                  </span>
                  <span className={classes.navText}>
                    <Typography className={classes.navLabel}>{t(area.label)}</Typography>
                    <span className={classes.navStatus}>
                      <span
                        className={classes.navStatusDot}
                        style={{ backgroundColor: toneDotColors[area.status.tone] }}
                      />
                      {t(area.status.label)}
                    </span>
                  </span>
                </button>
              );
            })}

            <div className={classes.navFooter}>
              <Typography className={classes.navFooterText}>
                {t('Only the essentials live here. For full control, open the complete legacy page from any area.')}
              </Typography>
              <Button variant="contained" size="small" className={classes.legacyButton} onClick={openLegacyLocations}>
                {t('All legacy settings')} &rarr;
              </Button>
            </div>
          </nav>

          <div className={classes.content}>
            {activeAreaElement}
          </div>
        </div>
      </Container>
    </>
  );
}

export default MainPreferencesModule;
